import { supabase } from "./supabase";

// Supabase's PostgrestFilterBuilder has deeply nested generic shapes we don't
// need to track here. Accept any chainable PostgREST builder via a generic so
// `.eq()`/`.is()`/`.gte()` typing stays intact at the call site.
type PostgrestQuery = ReturnType<ReturnType<typeof supabase.from>["select"]>;
type FilterFn = (q: PostgrestQuery) => PostgrestQuery;

export const PAGE_SIZE = 1000;

/**
 * Paginated fetch for tables that can exceed Supabase JS' 1000-row default cap.
 *
 * Supabase returns at most 1000 rows per request unless you page through with
 * `.range()`. A single un-paged `.select()` therefore SILENTLY truncates large
 * result sets — which is how the dashboard's "Logget" column undercounted hours
 * (team had 2825 time_entries, only 1000 came back). See VIB-946.
 *
 * Pages by a stable order column (default `id`) so range boundaries never skip
 * or duplicate rows between requests. Returns every matching row.
 */
export async function fetchAllPaginated<T>(
  table: string,
  columns: string,
  filters?: FilterFn,
  orderBy: string | string[] = "id",
): Promise<T[]> {
  const all: T[] = [];
  let from = 0;

  while (true) {
    let q = supabase.from(table).select(columns) as unknown as PostgrestQuery;
    for (const column of Array.isArray(orderBy) ? orderBy : [orderBy]) {
      q = q.order(column, { ascending: true });
    }
    q = q.range(from, from + PAGE_SIZE - 1) as unknown as PostgrestQuery;
    if (filters) q = filters(q);

    const { data, error } = await q;
    if (error) throw error;
    if (!data || data.length === 0) break;

    all.push(...(data as unknown as T[]));
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return all;
}
